import React from "react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n/LanguageProvider";
import {
  Truck,
  Warehouse,
  Factory,
  Cog,
  ShoppingCart,
  Package,
} from "lucide-react";

const ICONS = [Truck, Warehouse, Factory, Cog, ShoppingCart, Package];

export const Industries = () => {
  const { t } = useLang();

  return (
    <section
      id="industries"
      data-testid="industries-section"
      className="relative scroll-mt-24 overflow-hidden bg-[#0B1020] py-24 text-white md:py-32"
    >
      <div className="absolute inset-0 grid-bg opacity-40" />
      <div className="absolute -top-32 left-1/2 h-[500px] w-[700px] -translate-x-1/2 rounded-full bg-[#1E3A8A] opacity-30 blur-[140px]" />

      <div className="relative z-10 mx-auto max-w-7xl px-5 md:px-8">
        <div className="grid items-end gap-8 lg:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#A5B4FC]">
              {t.industries.eyebrow}
            </div>
            <h2 className="mt-5 font-display text-[clamp(2rem,4.2vw,3.25rem)] font-bold leading-tight tracking-tight">
              {t.industries.title}
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-base text-slate-300 md:text-lg"
          >
            {t.industries.subtitle}
          </motion.p>
        </div>

        <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {t.industries.items.map((item, i) => {
            const Icon = ICONS[i] || Truck;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: i * 0.07, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ y: -6, transition: { duration: 0.35, ease: [0.16, 1, 0.3, 1] } }}
                data-testid="industry-card"
                className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-7 backdrop-blur-md transition-colors hover:border-[#7C3AED]/50 hover:bg-white/[0.06]"
              >
                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#7C3AED]/60 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
                <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-[#7C3AED]/0 blur-3xl transition-all group-hover:bg-[#7C3AED]/30" />
                <div className="relative">
                  <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[#1E3A8A] to-[#7C3AED] text-white shadow-glow-purple">
                    <Icon className="h-7 w-7" />
                  </div>
                  <h3 className="mt-5 font-display text-2xl font-bold tracking-tight">{item.name}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-300">{item.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Industries;
